import React, { useEffect, useState } from 'react';
import { useSEO } from '../lib/useSEO';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { sessionsAPI } from '../lib/api';
import { useAuthStore } from '../lib/auth';
import { toast } from 'sonner';
import { Users, Copy, Play, ArrowLeft, Check } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

export default function GameLobby() {
  const { sessionId } = useParams();
  useSEO('Game Lobby | JepPrep', 'Waiting room for multiplayer trivia. Share the game code to invite players.');
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [isStarting, setIsStarting] = useState(false);

  useEffect(() => {
    loadSession();
    const interval = setInterval(loadSession, 3000); // Poll for new players
    return () => clearInterval(interval);
  }, [sessionId]);

  const loadSession = async () => {
    try {
      const response = await sessionsAPI.get(sessionId);
      setSession(response.data);
      
      // If game has started, redirect to play
      if (response.data.state?.phase !== 'lobby') {
        navigate(`/game/${sessionId}`);
      }
    } catch (error) {
      toast.error('Session not found');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const copyPin = () => {
    navigator.clipboard.writeText(session.pin);
    setCopied(true);
    toast.success('PIN copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const startGame = async () => {
    setIsStarting(true);
    try {
      await sessionsAPI.start(sessionId);
      toast.success('Game starting!');
      navigate(`/game/${sessionId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start game');
    } finally {
      setIsStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  if (!session) return null;

  const isHost = session.host_id === user?.id;
  const players = session.players || [];

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Navigation */}
      <nav className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-gray-300">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Leave
                </Button>
              </Link>
              <img src={LOGO_URL} alt="JepPrep" className="h-12" />
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="mx-auto max-w-2xl px-4 py-12">
        <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-display text-white mb-4">Game Lobby</CardTitle>
            <p className="text-gray-400">{session.board_title}</p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* PIN Display */}
            <div className="bg-[hsl(var(--brand-blue-800))] rounded-xl p-6 text-center">
              <p className="text-[hsl(var(--brand-gold-400))] text-sm mb-2">Share this PIN with players:</p>
              <div className="flex items-center justify-center gap-3">
                <span className="text-5xl font-mono font-bold text-white tracking-widest" data-testid="game-pin">
                  {session.pin}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copyPin}
                  className="text-[hsl(var(--brand-gold-400))]"
                  data-testid="copy-pin-button"
                >
                  {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </Button>
              </div>
              <p className="text-gray-400 text-sm mt-2">
                or visit: {window.location.origin}/join/{session.pin}
              </p>
            </div>

            {/* Players List */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Players ({players.length}/{session.config?.max_players || 3})
                </h3>
              </div>
              <div className="space-y-2">
                {players.map((player, idx) => (
                  <div
                    key={player.player_id}
                    className="flex items-center justify-between p-3 bg-[hsl(var(--accent))] rounded-lg"
                    data-testid="player-item"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[hsl(var(--brand-blue-800))] rounded-full flex items-center justify-center text-[hsl(var(--brand-gold-500))] font-bold">
                        {player.display_name?.charAt(0).toUpperCase() || '?'}
                      </div>
                      <span className="text-white">{player.display_name}</span>
                      {player.player_id === session.host_id && (
                        <span className="text-xs bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] px-2 py-0.5 rounded">Host</span>
                      )}
                    </div>
                    <span className="text-green-500 text-sm">● Connected</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Start Button (Host Only) */}
            {isHost ? (
              <Button
                onClick={startGame}
                disabled={isStarting || players.length < 1}
                className="w-full bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white py-6 text-lg"
                data-testid="start-game-button"
              >
                {isStarting ? (
                  'Starting...'
                ) : (
                  <>
                    <Play className="w-5 h-5 mr-2" />
                    Start Game
                  </>
                )}
              </Button>
            ) : (
              <div className="text-center text-gray-400 py-6">
                Waiting for host to start the game...
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
